import { useTranslation } from 'react-i18next';
import {
  useInvoice,
  useInvoiceDownload,
} from '@/features/deposit-bank-transfer';
import { INVOICE_FILE_TYPE } from '@/shared/config';
import type { PaidInvoiceDetailsModalProps } from './types';

const usePaidInvoiceDetails = ({
  invoice,
  lang,
}: Omit<PaidInvoiceDetailsModalProps, 'onBack'>) => {
  const { t } = useTranslation();

  const {
    data: invoiceDetails,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
  } = useInvoice({ invoiceId: invoice.invoiceId, lang });

  const {
    data: fileData,
    isLoading: isFileLoading,
    isError: isFileError,
    error: fileError,
    download,
    print,
    getFileSize,
    isConvertingToPdf,
    conversionError,
  } = useInvoiceDownload({
    invoiceId: invoice.invoiceId,
    fileType: INVOICE_FILE_TYPE,
    lang,
  });

  const fileName = `${invoice.bankName.replace(/\s+/g, '_')}.pdf`;

  const getFileSizeStr = () => {
    if (!fileData) return '';
    const size = getFileSize();
    return size ? `${(size / (1024 * 1024)).toFixed(2)} MB` : '';
  };

  const onDownload = () => {
    if (!fileData) return;
    download(fileName);
  };

  const onPrint = () => {
    if (!fileData) return;
    print();
  };

  const isError = isDetailsError || isFileError || Boolean(conversionError);

  const error = isDetailsError
    ? detailsError
    : isFileError
      ? fileError
      : conversionError
        ? new Error(t('common.unknownError'))
        : null;

  return {
    invoiceDetails,
    fileName,
    fileSizeStr: getFileSizeStr(),
    isLoading: isDetailsLoading || isFileLoading || isConvertingToPdf,
    isError,
    error,
    onDownload,
    onPrint,
  };
};

export { usePaidInvoiceDetails };
